"use client";

/**
 * @file        src/components/sections/FaqAccordion.tsx
 * @description The one reusable FAQ module for the whole site (series unified-faq). Renders a
 *              single-collapsible accordion with a framer-motion section entrance, visually
 *              identical to the previous home FAQ. Takes a resolved `items: FaqItem[]` (feed it
 *              `getFaqSet(page)` from @/data/faq); it does not import the catalogue itself.
 * @universal   knowledge/universals/components.md#FaqAccordion
 */

import * as m from "framer-motion/m";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { FaqItem } from "@/data/faq";

interface FaqAccordionProps {
  items: FaqItem[];
  heading?: string;
  eyebrow?: string;
  subheading?: string;
  id?: string;
}

export default function FaqAccordion({
  items,
  heading = "Частые вопросы",
  eyebrow = "Вопросы",
  subheading = "Ответы на самые популярные вопросы об импорте автомобилей",
  id = "faq",
}: FaqAccordionProps) {
  return (
    <section id={id} className="bg-white py-12 sm:py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-sm font-medium uppercase tracking-wider text-secondary">
            {eyebrow}
          </p>
          <h2 className="mt-2 font-heading text-2xl text-text sm:text-3xl md:text-4xl">
            {heading}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-text-muted sm:text-lg">
            {subheading}
          </p>
        </m.div>

        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="mx-auto mt-12 max-w-3xl"
        >
          <Accordion type="single" collapsible>
            {items.map((item) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                className="group mb-3 rounded-xl border border-border px-4 data-[state=open]:border-l-2 data-[state=open]:border-l-secondary"
              >
                <AccordionTrigger className="text-left text-sm font-medium text-text hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                {/*
                  @rule INV-F1 — `forceMount` keeps the answer in the DOM (and therefore in the
                  server-rendered HTML) whether the item is open or closed, so AI crawlers that run
                  no JS and click nothing can still read it. The collapse is driven from the parent
                  AccordionItem's `data-state` (it carries the `group` class): when the item is
                  closed, `group-data-[state=closed]:h-0` + overflow-hidden + py-0 give the
                  force-mounted content a persistent collapsed height so a closed item stays visually
                  hidden (INV-F5) — the primitive routes this className to the inner content div,
                  which has no `data-state` of its own, so the group ancestor drives it. Removing any
                  of these re-opens the P0 content-parity defect this module exists to fix, or makes
                  every answer render expanded.
                */}
                <AccordionContent
                  forceMount
                  className="text-sm text-text-muted group-data-[state=closed]:h-0 group-data-[state=closed]:overflow-hidden group-data-[state=closed]:py-0"
                >
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </m.div>
      </div>
    </section>
  );
}
